import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { sentOTP, verifyOTP } from "../Api/api";
import { useAuth } from './AuthContext';
import { motion } from 'framer-motion'; 
import { FaLocationArrow } from 'react-icons/fa';
import { useGeolocated } from "react-geolocated";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleLoginSuccess } = useAuth();
  const [credentials, setCredentials] = useState({
    countryCode: '+91',
    phoneNumber: '',
    otp: ['', '', '', ''],
  });
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [position, setPosition] = useState({
    lat: 0.0,
    lng: 0.0,
  });
  const [locationPermissionGranted, setLocationPermissionGranted] = useState(false);

  // Using react-geolocated for better location handling across platforms
  const { coords, isGeolocationAvailable, isGeolocationEnabled, getPosition } = useGeolocated({
    positionOptions: {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 10000,
    },
    watchPosition: false,
    onSuccess: (position) => {
      setPosition({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
      setLocationPermissionGranted(true);
    },
    onError: (error) => {
      console.error("Geolocation error:", error);
      // Try fallback to IP-based geolocation
      fetchIPBasedLocation();
    },
  });

  // Fallback to IP-based geolocation
  const fetchIPBasedLocation = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      
      if (data.latitude && data.longitude) {
        setPosition({
          lat: data.latitude,
          lng: data.longitude,
        });
        setLocationPermissionGranted(true);
        toast.info("Using approximate location");
      } else {
        throw new Error("Could not get location from IP");
      }
    } catch (error) {
      console.error("IP geolocation error:", error);
      toast.error("Location services unavailable. Please try again later or use a different device.");
      setLocationPermissionGranted(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  
  const handleOtpChange = (e, index) => {
    const value = e.target.value;
    if (value.length <= 1) {
      const newOtp = [...credentials.otp];
      newOtp[index] = value;
      setCredentials(prev => ({
        ...prev,
        otp: newOtp
      }));

      if (value && index < 3) {
        document.getElementById(`otp-input-${index + 1}`).focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !credentials.otp[index] && index > 0) {
      const newOtp = [...credentials.otp];
      newOtp[index - 1] = '';
      setCredentials(prev => ({
        ...prev,
        otp: newOtp
      }));
      document.getElementById(`otp-input-${index - 1}`).focus();
    }
  };
  
  const getLocation = () => {
    if (!isGeolocationAvailable) {
      toast.error("Your browser doesn't support location services. Using approximate location instead.");
      fetchIPBasedLocation();
      return;
    }
    
    if (!isGeolocationEnabled) {
      toast.warning("Location services are disabled. Using approximate location instead.");
      fetchIPBasedLocation();
      return;
    }
    
    getPosition();
  };
  
  const handleSubmitPhoneNumber = async (e) => {
    e.preventDefault();
    if (!locationPermissionGranted) {
      getLocation(); // Try to get location again if not granted
      return;
    }
    setLoading(true);
    try {
      await sentOTP(credentials.countryCode, credentials.phoneNumber);
      setOtpSent(true);
      toast.success("OTP sent successfully!");
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast.error("Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitOtp = async (e) => {
    e.preventDefault();
    if (!locationPermissionGranted) {
      getLocation(); // Try to get location again if not granted
      return;
    }
    setLoading(true);
    try {
      const otpValue = credentials.otp.join("");
      const response = await verifyOTP(
        credentials.countryCode,
        credentials.phoneNumber,
        otpValue,
        position
      );

      handleLoginSuccess(response);
      
      toast.success("Login successful!");
      const from = location.state?.from || "/dashboard";
      navigate(from, { replace: true });
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Invalid OTP or phone number");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    // Request location permission as soon as component mounts
    getLocation();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full mx-4"
      >
        <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-2xl p-8 space-y-6 border border-white/10">
          <div className="text-center space-y-4">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
              className="flex items-center justify-center gap-2"
            >
              <img
                src="/snabbit-logo.png"
                alt="Snabbit"
                className="h-10 w-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = "none";
                }}
              />
              <h2 className="text-3xl font-bold">
                <span className="text-pink-400">Snabbit</span>
                <span className="text-white"> Lead Gen</span>
              </h2>
            </motion.div>
            <p className="text-gray-300">Login or Sign up to continue</p>
            
            {/* Only show location warning if permission is denied */}
            {!locationPermissionGranted && (
              <div className="flex items-center justify-center gap-2 text-yellow-300 text-sm">
                <FaLocationArrow className="animate-pulse" />
                <span>Location access required for login</span>
              </div>
            )}
          </div>

          <form onSubmit={otpSent ? handleSubmitOtp : handleSubmitPhoneNumber} className="space-y-5">
            <div className="space-y-4">
              {!otpSent ? (
                <motion.div
                  className="relative flex items-center justify-between"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <input
                    id="countryCode"
                    name="countryCode"
                    type="text"
                    autoComplete="countryCode"
                    required
                    disabled
                    className="block w-12 pl-2 pr-2 py-3 mr-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-400/20 focus:border-pink-400/20 transition-all duration-200"
                    placeholder="Country code"
                    value={credentials.countryCode}
                    onChange={handleChange}
                  />
                  <input
                    id="phoneNumber"
                    name="phoneNumber"
                    type="tel"
                    autoComplete="tel"
                    required
                    className="block w-full pl-4 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-400/20 focus:border-pink-400/20 transition-all duration-200"
                    placeholder="Enter your phone number"
                    value={credentials.phoneNumber}
                    onChange={handleChange}
                  />
                </motion.div>
              ) : (
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="flex space-x-4 justify-center">
                    {Array(4).fill("").map((_, index) => (
                      <input
                        key={index}
                        id={`otp-input-${index}`}
                        type="text"
                        maxLength="1"
                        value={credentials.otp[index]}
                        onChange={(e) => handleOtpChange(e, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        className="w-16 h-16 text-center bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-400/20 focus:border-pink-400/20 transition-all duration-200"
                        placeholder="-"
                        autoFocus={index === 0}
                      />
                    ))}
                  </div>
                </motion.div>
              )}

              <motion.button
                type="submit"
                disabled={loading || !locationPermissionGranted}
                whileHover={{ scale: locationPermissionGranted ? 1.02 : 1 }}
                whileTap={{ scale: locationPermissionGranted ? 0.98 : 1 }}
                className={`w-full py-3 px-4 bg-pink-500 text-white rounded-lg font-medium shadow-lg transition-all duration-200 flex items-center justify-center ${
                  loading || !locationPermissionGranted ? "opacity-70 cursor-not-allowed bg-pink-500/50" : "hover:bg-pink-600"
                }`}
              >
                {loading ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    className="w-6 h-6 border-2 border-white border-t-transparent rounded-full"
                  />
                ) : otpSent ? (
                  "Verify OTP"
                ) : (
                  "Get Verification Code"
                )}
              </motion.button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;